import { ArrowLeftIcon } from 'lucide-react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { monthBounds } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { ExpensesSection } from '@/components/groups/sections/ExpensesSection';
import {
  ReportBarList,
  type ReportBarSlice,
} from '@/components/reports/ReportBarList';

import {
  useDeleteExpenseMutation,
  useGroupExpensesInfiniteQuery,
} from '@/hooks/queries/useExpenses';
import {
  useGroupCategoryReportQuery,
  useGroupMonthlyReportQuery,
} from '@/hooks/queries/useReports';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

import { useAuthStore } from '@/stores/authStore';

import { ExpenseCategoryIcon } from '@/utils/expenseCategoryIcons';

export type DrilldownFilter =
  { kind: 'category'; category: string } | { kind: 'month'; month: string };

interface ExpenseDrilldownReportProps {
  groupId: string;
  filter: DrilldownFilter;
  /** The reports page range (ISO dates). Ignored for the list when kind === 'month'. */
  startDate: string;
  endDate: string;
  currency: string;
  groupMembers?: Array<{
    userId: string;
    role: 'owner' | 'admin' | 'member';
  }>;
  onBack: () => void;
}

const PAGE_SIZE = 20;

/**
 * Slice key for the expenses that carry no subcategory. The API buckets
 * them under the category's own name (`expense.subcategory || category` in
 * GroupReportService.enrichWithSubcategories) and the older breakdown used
 * `'default'`, so both spellings land here — and neither can be sent back
 * as a `subcategory` filter, which is why this key never becomes an option.
 */
const NO_SUBCATEGORY = '__none__';

function monthLabel(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(year, monthNumber - 1, 1).toLocaleDateString(language, {
    month: 'long',
    year: 'numeric',
  });
}

export function ExpenseDrilldownReport({
  groupId,
  filter,
  startDate,
  endDate,
  currency,
  groupMembers,
  onBack,
}: ExpenseDrilldownReportProps) {
  const { t, i18n } = useTranslation('reports');
  useNamespace('expenses');
  useNamespace('groups');
  const user = useAuthStore(state => state.user);
  const [subcategory, setSubcategory] = useState('');

  const isCategory = filter.kind === 'category';
  const month = filter.kind === 'month' ? filter.month : undefined;
  const monthRange = month ? monthBounds(month) : null;

  const categoryReport = useGroupCategoryReportQuery(
    groupId,
    startDate.slice(0, 7),
    endDate.slice(0, 7),
    isCategory
  );
  // The reports page already fetched the monthly report for its own range,
  // so asking for the same range is a cache hit; the month is picked out of
  // the rows below.
  const monthlyReport = useGroupMonthlyReportQuery(
    groupId,
    startDate.slice(0, 7),
    endDate.slice(0, 7),
    !isCategory
  );

  const slices = useMemo<ReportBarSlice[]>(() => {
    if (filter.kind === 'category') {
      const row = categoryReport.data?.find(
        c => c.category === filter.category
      );
      if (!row) return [];
      const subs: ReportBarSlice[] = [];
      let noSubcategory = 0;
      for (const sub of row.subcategories ?? []) {
        if (
          sub.subcategory === filter.category ||
          sub.subcategory === 'default'
        ) {
          noSubcategory += sub.totalAmount;
          continue;
        }
        subs.push({
          key: sub.subcategory,
          name: t(
            `expenses:subcategories.${filter.category}.${sub.subcategory}`,
            { defaultValue: sub.subcategory }
          ),
          value: sub.totalAmount,
        });
      }
      // Fallback for a report whose subcategory rows do not add up to the
      // category total: whatever is missing is also subcategory-less, so it
      // joins the same bucket and the bars match the header amount.
      const remainder =
        row.totalAmount -
        subs.reduce((sum, sub) => sum + sub.value, 0) -
        noSubcategory;
      if (remainder > 0.005) noSubcategory += remainder;
      if (noSubcategory > 0.005) {
        subs.push({
          key: NO_SUBCATEGORY,
          name: t('drilldown.other'),
          value: noSubcategory,
        });
      }
      return subs;
    }
    const row = monthlyReport.data?.find(m => m.month === filter.month);
    if (!row) return [];
    return Object.entries(row.categories)
      .filter(([, value]) => value > 0)
      .sort(([, a], [, b]) => b - a)
      .map(([category, value]) => ({
        key: category,
        name: t(`expenses:categories.${category}`, { defaultValue: category }),
        value,
      }));
  }, [filter, categoryReport.data, monthlyReport.data, t]);

  const subcategoryOptions = useMemo(
    () => slices.filter(slice => slice.key !== NO_SUBCATEGORY),
    [slices]
  );

  const {
    data: expensesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useGroupExpensesInfiniteQuery(
    groupId,
    {
      category: filter.kind === 'category' ? filter.category : undefined,
      subcategory:
        filter.kind === 'category' && subcategory ? subcategory : undefined,
      startDate: monthRange ? monthRange.startDate : startDate,
      endDate: monthRange ? monthRange.endDate : endDate,
      limit: PAGE_SIZE,
    },
    true
  );

  const expenses = useMemo(
    () => expensesData?.pages.flatMap(page => page.expenses ?? []) ?? [],
    [expensesData]
  );
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const lastElementRef = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasMore: Boolean(hasNextPage),
    onLoadMore: loadMore,
  });

  // A filtered page can come back empty with more rows still to scan (the
  // API filters after DynamoDB's Limit). The table's sentinel row only
  // exists when there are rows, so nothing would ask for the next page.
  useEffect(() => {
    if (
      expenses.length === 0 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading
    ) {
      void fetchNextPage();
    }
  }, [
    expenses.length,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
  ]);

  const deleteExpense = useDeleteExpenseMutation();
  const handleDelete = useCallback(
    async (expenseId: string) => {
      try {
        await deleteExpense.mutateAsync({ expenseId, groupId });
      } catch {
        // Surfaced by useDeleteExpenseMutation's onError toast.
      }
    },
    [deleteExpense, groupId]
  );
  const noop = useCallback(() => undefined, []);

  const title =
    filter.kind === 'category'
      ? t(`expenses:categories.${filter.category}`, {
          defaultValue: filter.category,
        })
      : monthLabel(filter.month, i18n.language);
  const breakdownLoading = isCategory
    ? categoryReport.isLoading
    : monthlyReport.isLoading;

  return (
    <div className="space-y-6" data-testid="expense-drilldown-report">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t('drilldown.backToReports')}
      </button>

      <div className="flex items-center gap-3">
        {filter.kind === 'category' && (
          <ExpenseCategoryIcon
            category={filter.category}
            className="h-6 w-6 text-gray-500 dark:text-gray-400"
          />
        )}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {title}
        </h2>
      </div>

      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {t(isCategory ? 'drilldown.bySubcategory' : 'drilldown.byCategory')}
        </h3>
        {breakdownLoading ? null : slices.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {t('drilldown.noBreakdown')}
          </p>
        ) : (
          <ReportBarList slices={slices} currency={currency} />
        )}
      </section>

      {isCategory && subcategoryOptions.length > 0 && (
        <div className="flex items-center gap-3">
          <label
            htmlFor="drilldown-subcategory"
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('drilldown.subcategoryLabel')}
          </label>
          <select
            id="drilldown-subcategory"
            value={subcategory}
            onChange={e => setSubcategory(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">{t('drilldown.allSubcategories')}</option>
            {subcategoryOptions.map(option => (
              <option key={option.key} value={option.key}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <ExpensesSection
        groupId={groupId}
        title={t('drilldown.expenses')}
        expenses={expenses}
        loading={isLoading}
        hasMore={hasNextPage}
        onLoadMore={loadMore}
        onDelete={handleDelete}
        lastElementRef={lastElementRef}
        isFetchingNextPage={isFetchingNextPage}
        currentUserId={user?.id}
        groupMembers={groupMembers}
        onSearchChange={noop}
        emptyTitle={t('drilldown.noExpenses')}
        hideSearch
        hideFilters
      />
    </div>
  );
}
